// src/components/ProtectedRoute.tsx
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactElement;
}

// Full-screen loader used for the initial auth check (no UI rendered yet).
const FullScreenLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F7F7FA]">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-base text-gray-500">Loading...</p>
    </div>
  </div>
);

// Dimmed overlay shown over the app while permissions are being refreshed.
// The app content stays visible behind a translucent white veil, with a
// centered spinner + label — same pattern as Figma's reconnect state.
const RefreshOverlay: React.FC<{ label?: string }> = ({
  label = 'Refreshing permissions...',
}) => (
  <div
    className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-auto"
    style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', backdropFilter: 'blur(1px)' }}
    aria-busy="true"
    aria-live="polite"
  >
    <div className="flex items-center gap-3 px-5 py-3 rounded-lg bg-white shadow-md border border-[#E5E5EA]">
      <div className="w-6 h-6 border-[3px] border-teal-500 border-t-transparent rounded-full animate-spin" />
      <p className="text-sm font-medium text-[#1F1F1F]">{label}</p>
    </div>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, isRefreshingFromFocus } = useAuth();
  const location = useLocation();

  // Initial auth check — no user state yet, so a full-screen loader is fine.
  if (isLoading) {
    return <FullScreenLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  // Overlay only on tab re-activation (Figma-style). Cold loads, fresh logins,
  // and background mount refreshes do NOT show it.
  return (
    <>
      {children}
      {isRefreshingFromFocus && <RefreshOverlay />}
    </>
  );
};

export default ProtectedRoute;
